import { clockSeconds, clockTime, shortDate, relativeTime } from '../../shared/lib/format';
import type { TIssue, TIssueBin, TIssueDetail } from './api';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const dayLabel = (iso: string, timeZone?: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone }).format(
      date,
    );
  } catch {
    return shortDate(iso, timeZone);
  }
};

export const startedLabel = (iso: string, now: number, timeZone?: string): string => {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return iso;
  if (now - at < DAY) return clockTime(iso, timeZone);
  if (now - at < 2 * DAY) return 'yesterday';
  return dayLabel(iso, timeZone);
};

export const duration = (ms: number): string => {
  if (ms < MINUTE) return `${Math.max(0, Math.round(ms / 1000))} seconds`;
  if (ms < HOUR) {
    const minutes = Math.round(ms / MINUTE);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  if (ms < DAY) {
    const hours = Math.round(ms / HOUR);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  const days = Math.round(ms / DAY);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
};

export const plural = (count: number, one: string, many: string): string =>
  count === 1 ? one : many;

export const SPARK_BUCKETS = 24;
export const SPARK_WINDOW_MS = 6 * HOUR;

export const sparkBuckets = (
  bins: readonly TIssueBin[],
  now: number,
  windowMs = SPARK_WINDOW_MS,
  buckets = SPARK_BUCKETS,
): number[] => {
  const start = now - windowMs;
  const step = windowMs / buckets;
  const out = Array.from({ length: buckets }, () => 0);
  for (const bin of bins) {
    const index = Math.floor((bin.t - start) / step);
    if (index >= 0 && index < buckets) out[index] += bin.n;
  }
  return out;
};

export type TIssueTone = 'danger' | 'motion' | 'muted';

export const issueTone = (
  state: TIssue['state'],
  quietMs: number,
  liveWindowMs: number,
): TIssueTone => {
  if (state === 'resolved' || state === 'ignored') return 'muted';
  if (state === 'open') return 'danger';
  return quietMs <= liveWindowMs ? 'danger' : 'motion';
};

const subject = (issue: TIssue | TIssueDetail): string =>
  issue.flowName ?? issue.sourceName ?? 'This source';

export interface IIssueStatement {
  tense: 'present' | 'past';
  sentence: string;
  subtitle: string;
  quietFor: string | null;
}

export const issueStatement = (
  issue: TIssueDetail,
  now: number,
  timeZone?: string,
): IIssueStatement => {
  const who = subject(issue);
  const since = clockTime(issue.firstSeen, timeZone);
  const until = clockTime(issue.lastSeen, timeZone);
  const sinceDay = dayLabel(issue.firstSeen, timeZone);
  const quietMs = now - new Date(issue.lastSeen).getTime();
  const live = quietMs <= Math.max(issue.binMs, MINUTE);
  const spanStartedToday = now - new Date(issue.firstSeen).getTime() < DAY;
  const from = spanStartedToday ? since : `${sinceDay} ${since}`;
  const count = `${issue.eventCount} ${issue.eventCount === 1 ? 'event' : 'events'}`;
  const span = since === until ? `at ${from}` : `between ${from} and ${until}`;

  if (issue.state === 'ignored') {
    return {
      tense: 'past',
      sentence: `${who} has failed ${count} since ${sinceDay}. You chose not to be told about this one.`,
      subtitle: `Hidden since ${sinceDay} · last failure ${relativeTime(issue.lastSeen, now, timeZone)} · ${count}`,
      quietFor: duration(quietMs),
    };
  }

  if (issue.state === 'resolved') {
    const how =
      issue.resolution === 'manual'
        ? 'A person closed it.'
        : issue.resolution === 'auto'
          ? 'It recovered on its own; nobody intervened.'
          : 'It is closed.';
    return {
      tense: 'past',
      sentence: `${who} failed ${span}. ${how}`,
      subtitle: `Started ${clockSeconds(issue.firstSeen, timeZone)} · ${
        issue.resolvedAt ? `closed ${relativeTime(issue.resolvedAt, now, timeZone)}` : 'closed'
      } · ${count}`,
      quietFor: duration(quietMs),
    };
  }

  if (live) {
    return {
      tense: 'present',
      sentence: `${who} is failing. It first showed up at ${from} and was last seen ${relativeTime(issue.lastSeen, now, timeZone)}.`,
      subtitle: `Started ${clockSeconds(issue.firstSeen, timeZone)} · still happening · ${count}`,
      quietFor: null,
    };
  }

  return {
    tense: 'past',
    sentence: `${who} failed ${span}. Nothing has failed in the ${duration(quietMs)} since.`,
    subtitle: `Started ${clockSeconds(issue.firstSeen, timeZone)} · quiet for ${duration(quietMs)} · ${count}`,
    quietFor: duration(quietMs),
  };
};
